import { useMutation, useQuery } from "@tanstack/react-query";
import queryString from "query-string";

import { api } from "@services/index";
import {
  ApiErrorModel,
  PrivateUseModelDto,
  PrivateUseModel,
} from "@interfaces/index";

export const usePrivateUseGetAll = ({
  puEnabled,
  businessId = "",
  submissionId = "",
}: {
  puEnabled: boolean;
  businessId?: string;
  submissionId?: string;
}) => {
  const queryFn = async () => {
    const params = queryString.stringify(
      {
        user_business_unique_id: businessId,
        submission_unique_id: submissionId,
      },
      { skipEmptyString: true },
    );
    const { data: dataDto } = await api.get<{
      items: PrivateUseModelDto[];
      total: string;
      total_business_expenses: string;
      total_private_expenses: string;
    }>(`/private-use?${params}`);

    const data = {
      items: dataDto.items.map((item) => ({
        categoryId: item.category_unique_id,
        categoryTitle: item.category_title,
        categoryType: item.category_type,
        assets: item.assets_private_use.map((asset) => ({
          assetId: asset.asset_unique_id,
          assetName: asset.asset_name,
          totalAmount: asset.total_amount,
          mileagePrivateUse: asset.mileage_private_use,
          privateUseId: asset.private_use_id,
          privateUse: asset.private_use,
          privateExpenses: asset.private_expenses,
          businessExpenses: asset.business_expenses,
          policy: {
            canCreate: asset.policy.canCreate,
            canUpdate: asset.policy.canUpdate,
          },
        })),
      })),
      total: dataDto.total,
      totalBusinessExpenses: dataDto.total_business_expenses,
      totalPrivateExpenses: dataDto.total_private_expenses,
    };

    return data;
  };

  return useQuery<
    {
      items: PrivateUseModel[];
      total: string;
      totalBusinessExpenses: string;
      totalPrivateExpenses: string;
    },
    ApiErrorModel
  >({
    queryKey: ["privateUseGetAll", businessId, submissionId],
    queryFn,
    refetchOnWindowFocus: true,
    enabled: puEnabled && !!businessId && !!submissionId,
  });
};

export const usePrivateUseCreate = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    businessId,
    assetId,
    categoryId,
    submissionId,
    percentage,
  }: {
    businessId: string;
    assetId: string;
    categoryId: string;
    submissionId: string;
    percentage: string;
  }) => {
    const fieldsDto = {
      user_business_unique_id: businessId,
      asset_unique_id: assetId,
      category_unique_id: categoryId,
      submission_unique_id: submissionId,
      percentage,
    };
    await api.post("/private-use", fieldsDto);
  };

  return useMutation({
    mutationKey: ["privateUseCreate"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      if (onError) {
        onError({ ...error });
      }
    },
  });
};

export const usePrivateUseUpdate = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    privateUseId,
    percentage,
  }: {
    privateUseId: string;
    percentage: string;
  }) => {
    await api.patch(`/private-use/${privateUseId}`, { percentage });
  };

  return useMutation({
    mutationKey: ["privateUseUpdate"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      if (onError) {
        onError({ ...error });
      }
    },
  });
};
